import { motion } from 'framer-motion';
import { Edit2, Trash2, Eye, EyeOff, Languages, Loader2 } from 'lucide-react';

/**
 * Single blog post card in the admin list
 */
export const BlogPostCard = ({
  post,
  isTranslating,
  onEdit,
  onDelete,
  onTogglePublished,
  onTranslate,
  formatDate
}) => {
  const hasAllTranslations = post.title_en && post.title_fr && post.title_de;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-lg transition-all"
      data-testid={`post-card-${post.id}`}
    >
      <div className="flex gap-4">
        {/* Thumbnail */}
        {post.image_url && (
          <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
            <img
              src={post.image_url}
              alt={post.title_nl || 'Blog post thumbnail'}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-semibold text-[#0f172a] line-clamp-1">
                {post.title_nl || 'Geen titel'}
              </h3>
              <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                {post.excerpt_nl || 'Geen samenvatting'}
              </p>
            </div>
            
            {/* Status badges */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  post.is_published
                    ? 'bg-green-100 text-green-700'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                {post.is_published ? 'Gepubliceerd' : 'Concept'}
              </span>
              {!hasAllTranslations && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                  Niet vertaald
                </span>
              )}
            </div>
          </div>

          {/* Meta info */}
          <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
            <span>{post.category || 'Geen categorie'}</span>
            <span>•</span>
            <span>{formatDate(post.created_at)}</span>
            {post.tags?.length > 0 && (
              <>
                <span>•</span>
                <span>{post.tags.slice(0, 3).join(', ')}</span>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={() => onEdit(post)}
              className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 hover:text-[#2563EB] transition-colors"
              title="Bewerken"
              data-testid={`edit-post-${post.id}`}
            >
              <Edit2 size={18} />
            </button>
            
            <button
              onClick={() => onTogglePublished(post)}
              className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 hover:text-[#2563EB] transition-colors"
              title={post.is_published ? 'Verbergen' : 'Publiceren'}
            >
              {post.is_published ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
            
            <button
              onClick={() => onTranslate(post.id)}
              disabled={isTranslating}
              className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 hover:text-[#2563EB] transition-colors disabled:opacity-50"
              title="AI Vertalen"
            >
              {isTranslating ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Languages size={18} />
              )}
            </button>
            
            <button
              onClick={() => onDelete(post.id)}
              className="p-2 hover:bg-red-50 rounded-lg text-slate-600 hover:text-red-600 transition-colors"
              title="Verwijderen"
              data-testid={`delete-post-${post.id}`}
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BlogPostCard;
